import { clamp } from './util';
import { RenderToken, Token } from './parser';

const typingCharactersPerSecond = 15;
export const linesPerSecond = 15;
export const baselineTimePerLine = 1000 / linesPerSecond;

export function timeToRenderLine(tokens: readonly RenderToken[]): number {
  let lineTime = baselineTimePerLine;
  for (const token of tokens) {
    if (token.kind === Token.Wait) {
      lineTime += token.time;
    } else if (token.kind === Token.Loading) {
      lineTime += token.time;
    } else if (token.kind === Token.Typing) {
      const chars = Array.from(token.text).length;
      lineTime += (chars * 1000) / typingCharactersPerSecond;
    }
  }
  return lineTime;
}

export function timeToRenderAll(lines: readonly RenderToken[][]): number {
  let total = 0;
  for (const line of lines) {
    total += timeToRenderLine(line);
  }
  return total;
}

export function computeNextScroll(
  state: {
    lines: RenderToken[][];
    renderLineTime: number;
    lastLineShown: number;
  },
  showOneInProgress = true,
): { nextLastShown: number; shouldClick: boolean } {
  let linesToScroll = 0;
  let accumulatedTime = 0;

  for (const line of state.lines) {
    const lineTime = timeToRenderLine(line);
    if (accumulatedTime + lineTime > state.renderLineTime) break;
    accumulatedTime += lineTime;
    linesToScroll++;
  }

  if (showOneInProgress) {
    linesToScroll++;
  }

  linesToScroll = clamp(linesToScroll, 0, state.lines.length);
  const nextLastShown = linesToScroll;

  let shouldClick = false;
  if (state.lastLineShown !== nextLastShown) {
    const prev = state.lastLineShown;
    const prevLine = state.lines[prev];
    if (nextLastShown === 1 && prevLine !== undefined && prevLine.length === 1 && prevLine[0]?.kind === Token.Wait) {
      shouldClick = false;
    } else {
      shouldClick = true;
    }
  }

  return { nextLastShown, shouldClick };
}

/**
 * Useful for later per-line budget math. Returns an array where:
 * cumulative[i] = time to render lines[0..i-1], with cumulative[0] = 0.
 */
export function buildCumulativeTimes(
  lines: readonly RenderToken[][],
  timeOfLine: (tokens: readonly RenderToken[]) => number = timeToRenderLine,
): number[] {
  const cumulative: number[] = [0];
  for (let i = 0; i < lines.length; i++) {
    const prev = cumulative[i] ?? 0;
    cumulative[i + 1] = prev + timeOfLine(lines[i]!);
  }
  return cumulative;
}

/**
 * True when we are fully scrolled to the end and should reserve a row for the prompt.
 */
export function shouldReservePromptRow(state: { lastLineShown: number; lines: unknown[] }) {
  return state.lastLineShown === state.lines.length;
}

/**
 * Compute how many lines can fit vertically, optionally reserving one row for the prompt.
 */
export function computeMaxVisibleLines(
  height: number,
  margin: number,
  textHeight: number,
  lineSpacing: number,
  reservePromptRow: boolean,
) {
  return Math.floor((height - margin * 2) / (textHeight + lineSpacing) - (reservePromptRow ? 1 : 0));
}

/**
 * Select a tail slice of lines to render based on the current scroll position and max visible rows.
 * Returns both the slice and the base global index for budget math.
 */
export function selectLinesToRender(
  state: { lines: readonly RenderToken[][]; lastLineShown: number; scrolled: number | null },
  maxVisible: number,
) {
  if (state.scrolled === null) {
    const linesToShow = state.lines.slice(0, state.lastLineShown);
    const linesToRender = linesToShow.slice(-maxVisible);
    const baseGlobalIndex = linesToShow.length - linesToRender.length;
    return { linesToRender, baseGlobalIndex };
  } else {
    // TODO: implement scroll here
    // clamp scroll here?
    state.scrolled = clamp(state.scrolled, maxVisible, state.lastLineShown);
    const linesToShow = state.lines.slice(0, state.scrolled);
    const linesToRender = linesToShow.slice(-maxVisible);
    const baseGlobalIndex = linesToShow.length - linesToRender.length;

    if (state.scrolled === state.lastLineShown) {
      // scrolled to bottom, so should resticky
      state.scrolled = null;
    }
    return { linesToRender, baseGlobalIndex };
  }
}

/**
 * Compute the time budget for a line given total render time and cumulative prefix times.
 */
export function computeLineBudgetMs(
  renderLineTime: number,
  cumulativeTimes: readonly number[],
  globalIndex: number,
): number {
  const consumedBefore = cumulativeTimes[globalIndex] ?? 0;
  const remaining = renderLineTime - consumedBefore;
  return remaining > 0 ? remaining : 0;
}
